import { style } from '@vanilla-extract/css';
import { recipe } from '@vanilla-extract/recipes';
import { vars } from '@/shared/ui/theme';
import { reducedMotion, mobile } from '@/shared/ui/theme';

/**
 * 화면·창 전환 탭 — DESIGN.md §Visual Hierarchy Rules 의 TIER 1(탐색)이라 rim + glow 를 갖는다.
 * 세기를 낮추는 톤 축은 두지 않는다. 조용해야 하는 것은 탭이 아니라 필터·정렬·폼 값이고,
 * 그건 `Button variant="chip"` · `Radio` 의 일이다(SegmentedControl.tsx 상단 주석).
 */
export const group = style({
  display: 'flex',
  /*
    레일은 칩 개수만큼만 넓다. `width` 를 안 적으면 flex-column 부모(예: 로스터 콘솔의
    `tabbed`)의 `align-items: stretch` 가 이 상자를 행 전체로 늘려, 칩 둘이 왼쪽에 몰린
    1,800px 짜리 빈 알약이 그려진다. 선례는 TeamOpsConsole.css.ts 의 `segment`(inline-flex)
    — flex 아이템은 blockify 되어 inline-flex 가 안 먹으므로 명시 폭으로 잡는다.
    fit-content 는 가용 폭에서 잘리므로 모바일 가로 스크롤 레일도 그대로 선다.
  */
  width: 'fit-content',
  gap: vars.spacing.gap['2xs'],
  background: vars.bright.overlay.dark,
  borderRadius: vars.spacing.borderRadius.xl,
  padding: vars.spacing.padding.xs,
  border: `1px solid ${vars.surface.highlight.strong}`,
  '@media': {
    [mobile]: {
      overflowX: 'auto',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
    },
  },
});

export const button = recipe({
  base: {
    padding: '6px 14px',
    borderRadius: vars.spacing.borderRadius.lg,
    border: 'none',
    background: 'transparent',
    color: vars.typography.textColor.tertiary,
    fontSize: vars.typography.fontSize.sm,
    fontWeight: vars.typography.fontWeight.semibold,
    fontFamily: vars.typography.fontFamily.mono,
    cursor: 'pointer',
    transition: `color ${vars.animation.duration.fast}, background ${vars.animation.duration.fast}`,
    display: 'flex',
    alignItems: 'center',
    gap: vars.spacing.gap.sm,
    whiteSpace: 'nowrap',
    position: 'relative',
    selectors: {
      '&:hover': {
        color: vars.typography.textColor.secondary,
        background: vars.surface.highlight.light,
      },
    },
    '@media': {
      [reducedMotion]: { transition: 'none' },
      [mobile]: {
        fontSize: vars.typography.fontSize.xs,
        minHeight: '36px',
        /*
          누르는 영역만 44px(WCAG 2.5.5)로 늘린다. 칩은 36px 로 보이고 모자란 8px 은 이 의사
          요소가 채운다. 레일이 모바일에서 overflowX:auto 라 넘친 부분은 레일 padding box 에서
          잘리지만 36 + 레일 여백 4×2 = 44 라 남는다(SegmentedControl.touchTarget.test.ts).
          데스크톱에선 레일 밖으로 넘쳐 옆 요소(타이틀바 드래그 등)를 가리므로 모바일에만 둔다.
        */
        '::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          top: '50%',
          height: '44px',
          transform: 'translateY(-50%)',
        },
      },
    },
  },
  variants: {
    active: {
      true: {
        background: `linear-gradient(135deg, ${vars.surface.highlight.strong} 0%, ${vars.stateColor.info.light} 100%)`,
        color: vars.bright.primary.light,
        boxShadow: `${vars.bright.glow.primary}, inset 0 1px 0 ${vars.bright.overlay.lighter}`,
      },
      false: {},
    },
    /*
      size — 밀도 축.

      기본 `md` 는 모노 14px · 6×14 라 페이지 폭을 쓰는 탭 줄에 맞는데, 표 옆에 앉는
      밀집 컨트롤(로스터 콘솔의 탭 줄)에서는 표보다 먼저 읽힌다. `sm` 은 DESIGN.md
      §Type Scale 의 xs(12px) · §Border Radius 의 md(4px) 로 한 단 내린 변형이다.
      가로 10px 은 토큰 밖 값인데, base 의 `6px 14px` 가 이미 그렇다 — 칩 패딩은
      간격 스케일이 아니라 글자 크기에 붙는 값이라 이 파일의 기존 선례를 따른다.
    */
    size: {
      md: {},
      sm: {
        fontSize: vars.typography.fontSize.xs,
        padding: '4px 10px',
        borderRadius: vars.spacing.borderRadius.md,
      },
    },
  },
  defaultVariants: { active: false, size: 'md' },
});

export const badge = recipe({
  base: {
    fontWeight: vars.typography.fontWeight.bold,
    background: vars.bright.status.warning,
    color: vars.typography.textColor.inverse,
    borderRadius: vars.spacing.borderRadius.full,
    padding: '1px 6px',
    lineHeight: vars.typography.lineHeight.tight,
  },
  variants: {
    // 배지는 칩 글자보다 한 단 아래로 따라 내려간다 — md 는 12px, sm 은 11px.
    size: {
      md: { fontSize: vars.typography.fontSize.xs },
      sm: { fontSize: vars.typography.fontSize['2xs'] },
    },
    // 뜻 축. alert = 처리할 건수(정산 대기 · 오늘 활동), count = 서랍의 크기(시나리오 진행 12 · 이력 22).
    // 서랍 크기에 amber 채움을 달면 "처리할 34건" 으로 읽힌다 — docs/design/ui/scenario-dossier-registry-design.md §2.
    tone: {
      alert: {},
      count: {
        background: 'transparent',
        color: vars.typography.textColor.tertiary,
        fontWeight: vars.typography.fontWeight.regular,
        padding: 0,
        selectors: {
          '[aria-selected="true"] &': { color: vars.typography.textColor.secondary },
        },
      },
    },
  },
  defaultVariants: { size: 'md', tone: 'alert' },
});
